import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { apiFetch } from "../../../config/api";
import Loader from "../../../components/Loader";
import StatusPill from "../../../components/StatusPill";
import { getUser } from "../../../config/auth";
import { PRIORITY_TAG_BG, PRIORITY_TAG_COLORS } from "../../../config/constants";

/**
 * ✅ FIX: Authenticated file opener.
 *
 * Problem: <a href="...api/evidence/file.jpg"> opens a new tab WITHOUT the
 * Authorization header, so the server returns 401.
 *
 * Solution: Use apiFetch (which attaches the Bearer token) to download the
 * file as a Blob, create a temporary object URL, and programmatically open
 * or download it. The object URL is revoked after use to free memory.
 *
 * Also handles legacy DB entries that stored full URLs instead of filenames.
 */
function EvidenceFileLink({ taskId, fileName }) {
  const [loading, setLoading] = useState(false);
  const [fileError, setFileError] = useState("");
  const mountedRef = useRef(true);
  const timerRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleOpen = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (mountedRef.current) {
      setLoading(true);
      setFileError("");
    }
    try {
      const res = await apiFetch(
        `/api/tasks/${taskId}/evidence/${encodeURIComponent(fileName)}`,
      );
      if (!res.ok) {
        if (res.status === 404) {
          if (mountedRef.current) setFileError("File not found on server. It may have been uploaded with an older version.");
        } else {
          if (mountedRef.current) setFileError(`Could not open file (${res.status}). Please try again.`);
        }
        return;
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);

      // Images and PDFs open inline; everything else triggers a download
      const ext = fileName.split(".").pop().toLowerCase();
      const openInline = ["pdf", "png", "jpg", "jpeg"].includes(ext);

      if (timerRef.current) clearTimeout(timerRef.current);
      if (openInline) {
        window.open(objectUrl, "_blank");
        // Give the new tab a moment to load before revoking
        timerRef.current = setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
      } else {
        // Force download
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        timerRef.current = setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
    } catch (err) {
      if (mountedRef.current) setFileError("Error opening file: " + err.message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={handleOpen}
        disabled={loading}
        style={{
          fontSize: 13,
          color: loading ? "#9ca3af" : "#2563eb",
          textDecoration: "none",
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 10px",
          background: loading ? "#f9fafb" : "#f0f9ff",
          borderRadius: 8,
          border: `1px solid ${loading ? "#e5e7eb" : "#bae6fd"}`,
          cursor: loading ? "not-allowed" : "pointer",
          textAlign: "left",
          width: "100%",
        }}
      >
        {loading ? "⏳" : "📄"} {fileName}
        {loading && (
          <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-3)" }}>
            Opening...
          </span>
        )}
      </button>
      {fileError && (
        <div style={{ marginTop: 4, fontSize: 12, color: "var(--danger-text)", padding: "4px 8px", background: "var(--danger-soft)", borderRadius: 6 }}>
          {fileError}
        </div>
      )}
    </>
  );
}

export default function TaskDetails() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [project, setProject] = useState(undefined); // undefined=loading, null=failed/not found

  // ✅ GAP-7: Evidence files
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [evidenceUrlLabel, setEvidenceUrlLabel] = useState("");
  const [addingUrl, setAddingUrl] = useState(false);
  const [evidenceTab, setEvidenceTab] = useState("url"); // "url" | "file"
  const [evidenceError, setEvidenceError] = useState("");

  // ✅ GAP-10: Timeline change
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineDate, setTimelineDate] = useState("");
  const [timelineReason, setTimelineReason] = useState("");
  const [submittingTimeline, setSubmittingTimeline] = useState(false);
  const [timelineError, setTimelineError] = useState("");
  const [timelineRequests, setTimelineRequests] = useState([]);

  const [statusError, setStatusError] = useState("");
  const [commentError, setCommentError] = useState("");

  // @mention autocomplete
  const [mentionQuery,  setMentionQuery]  = useState("");
  const [showMentions,  setShowMentions]  = useState(false);

  const [editingField, setEditingField] = useState(null); // 'title' | 'assignee' | 'dueDate' | 'priority'
  const [editValue, setEditValue] = useState("");
  const [savingField, setSavingField] = useState(false);
  const [fieldError, setFieldError] = useState("");
  const [assignableUsers, setAssignableUsers] = useState([]);

  // Review comment modal (replaces window.prompt)
  const [reviewModal, setReviewModal] = useState(null); // { requestId, action }
  const [reviewComment, setReviewComment] = useState("");
  const [reviewError, setReviewError] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const user = getUser();
  const role = user?.role || "Staff";
  const isPrivileged = role !== "Staff";
  const isAdmin = role === "Admin";

  const isAssignee = task?.assignedToUserId === user?.id;
  const isAssigneesManager = role === "Manager" && task?.assignedToUserReportsToId === user?.id;
  const isAdminOrAbove = role === "Admin" || role === "Director" || role === "CXO";
  // canEdit: Admin/CXO can edit anything; everyone else only if they assigned the task
  const canEdit = isAdminOrAbove || user?.id === task?.assignedByUserId;

  // Start Task / Submit for Validation: assignee + their reporting manager + Admin/Director/CXO
  const canAdvanceStatus = isAssignee || isAssigneesManager || isAdminOrAbove;

  // Validate & Close: manager of assignee + Admin/Director/CXO
  const canValidateAndClose =
    isAdminOrAbove ||
    (role === "Manager" && task?.assignedToUserReportsToId === user?.id);

  // Evidence upload: assignee only
  const canAddEvidence = isAssignee;

  // Comments: assignee + their manager + Admin/Director/CXO
  const canComment = isAssignee || isAssigneesManager || isAdminOrAbove;

  // Timeline review: Head/CXO/Director/Admin + manager of assignee
  const canReviewTimeline =
    role === "Head" || isAdminOrAbove ||
    (role === "Manager" && task?.assignedToUserReportsToId === user?.id);

  useEffect(() => {
    if (isPrivileged) {
      apiFetch("/api/users/assignable")
        .then(r => r.ok ? r.json() : [])
        .then(data => setAssignableUsers(Array.isArray(data) ? data : []))
        .catch(() => {});
    }
  }, [isPrivileged]);

  // ✅ BUG FIX #2: Load evidence from dedicated endpoint instead of parsing embedded JSON string
  useEffect(() => {
    setLoading(true);
    setLoadingComments(true);
    setProject(undefined); // reset to loading state on taskId change

    apiFetch(`/api/tasks/${taskId}`)
      .then((r) => {
        if (!r.ok) throw new Error("Failed to load task");
        return r.json();
      })
      .then((data) => {
        setTask(data);
        if (data.projectId) {
          apiFetch(`/api/projects/${data.projectId}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((p) => setProject(p))
            .catch(() => setProject(null));
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    // ✅ BUG FIX #2: Always load evidence files from their own clean endpoint
    apiFetch(`/api/tasks/${taskId}/evidence`)
      .then((r) => (r.ok ? r.json() : { files: [] }))
      .then((data) =>
        setEvidenceFiles(Array.isArray(data.files) ? data.files : []),
      )
      .catch(() => setEvidenceFiles([]));

    apiFetch(`/api/tasks/${taskId}/comments`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setComments(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setLoadingComments(false));

    // ✅ GAP-10: Load timeline change requests
    apiFetch(`/api/tasks/${taskId}/timeline-changes`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setTimelineRequests(Array.isArray(data) ? data : []))
      .catch(() => setTimelineRequests([]));
  }, [taskId]);

  // ✅ BUG FIX #2: After upload, reload evidence list from clean endpoint
  const uploadEvidence = async (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
    for (const f of files) {
      if (f.size > MAX_SIZE) {
        setEvidenceError(`"${f.name}" exceeds the 10 MB file size limit.`);
        return;
      }
    }
    setUploadingFile(true);
    setEvidenceError("");
    try {
      const formData = new FormData();
      for (const f of files) formData.append("files", f);
      const res = await apiFetch(`/api/tasks/${taskId}/evidence`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error("Upload failed");
      const listRes = await apiFetch(`/api/tasks/${taskId}/evidence`);
      if (listRes.ok) {
        const data = await listRes.json();
        setEvidenceFiles(Array.isArray(data.files) ? data.files : []);
        e.target.value = ""; // Reset so the same file can be re-uploaded if needed
      }
    } catch (err) {
      setEvidenceError("File upload failed: " + err.message);
    } finally {
      setUploadingFile(false);
    }
  };

  const submitEvidenceUrl = async () => {
    if (!evidenceUrl.trim()) return;
    if (!/^https?:\/\//i.test(evidenceUrl.trim())) {
      setEvidenceError("URL must start with http:// or https://");
      return;
    }
    setAddingUrl(true);
    setEvidenceError("");
    try {
      const label = evidenceUrlLabel.trim() || evidenceUrl.trim();
      // Store as JSON-encoded link entry in the evidence list
      const res = await apiFetch(`/api/tasks/${taskId}/evidence/link`, {
        method: "POST",
        body: JSON.stringify({ url: evidenceUrl.trim(), label }),
      });
      if (!res.ok) throw new Error(await res.text());
      const listRes = await apiFetch(`/api/tasks/${taskId}/evidence`);
      if (listRes.ok) {
        const data = await listRes.json();
        setEvidenceFiles(Array.isArray(data.files) ? data.files : []);
      }
      setEvidenceUrl("");
      setEvidenceUrlLabel("");
    } catch (err) {
      setEvidenceError("Failed to add link: " + err.message);
    } finally {
      setAddingUrl(false);
    }
  };

  // ✅ GAP-10: Submit timeline change request
  const submitTimelineRequest = async () => {
    if (!timelineDate || !timelineReason.trim()) {
      setTimelineError("Please provide a new date and reason.");
      return;
    }
    setTimelineError("");
    setSubmittingTimeline(true);
    try {
      const res = await apiFetch(`/api/tasks/${taskId}/timeline-changes`, {
        method: "POST",
        body: JSON.stringify({
          requestedDueDate: timelineDate,
          reason: timelineReason,
        }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg);
      }
      const data = await res.json();
      setTimelineRequests((prev) => [data, ...prev]);
      setShowTimelineModal(false);
      setTimelineDate("");
      setTimelineReason("");
      setTimelineError("");
    } catch (err) {
      setTimelineError("Could not submit: " + err.message);
    } finally {
      setSubmittingTimeline(false);
    }
  };

  // ✅ GAP-10: Approve / reject timeline request — returns true on success
  const reviewTimeline = async (requestId, action, comment) => {
    setReviewError("");
    try {
      const res = await apiFetch(
        `/api/tasks/${taskId}/timeline-changes/${requestId}/${action}`,
        { method: "PUT", body: JSON.stringify({ comment: comment || "" }) },
      );
      if (!res.ok) throw new Error(await res.text() || "Review failed");
      // Re-fetch both task and timeline requests so UI is fully up to date
      const [tRes, tlRes] = await Promise.all([
        apiFetch(`/api/tasks/${taskId}`),
        apiFetch(`/api/tasks/${taskId}/timeline-changes`),
      ]);
      if (tRes.ok) setTask(await tRes.json());
      if (tlRes.ok) {
        const tlData = await tlRes.json();
        setTimelineRequests(Array.isArray(tlData) ? tlData : []);
      }
      return true;
    } catch (err) {
      setReviewError(err.message || "Failed to submit review");
      return false;
    }
  };

  const openReviewModal = (requestId, action) => {
    setReviewComment("");
    setReviewError("");
    setReviewModal({ requestId, action });
  };

  const submitReview = async () => {
    if (!reviewModal || submittingReview) return;
    setSubmittingReview(true);
    const ok = await reviewTimeline(reviewModal.requestId, reviewModal.action, reviewComment);
    setSubmittingReview(false);
    if (ok) {
      setReviewModal(null);
      setReviewComment("");
      setReviewError("");
    }
  };

  const startEdit = (field) => {
    if (!canEdit || savingField) return;
    setFieldError("");
    setEditingField(field);
    if (field === "title") setEditValue(task.title || "");
    if (field === "assignee") setEditValue(task.assignedToUserId || "");
    if (field === "dueDate") setEditValue(task.dueDate ? task.dueDate.split("T")[0] : "");
    if (field === "priority") setEditValue(task.priority || "Medium");
    if (field === "brief") setEditValue(task.brief || "");
  };

  const saveField = async (field, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (field === "title" && !trimmed) { setEditingField(null); return; }
    if (field === "title" && trimmed === task.title) { setEditingField(null); return; }
    if (field === "assignee" && trimmed === task.assignedToUserId) { setEditingField(null); return; }
    if (field === "dueDate" && trimmed === (task.dueDate ? task.dueDate.split("T")[0] : "")) { setEditingField(null); return; }
    if (field === "priority" && trimmed === task.priority) { setEditingField(null); return; }
    if (field === "brief" && trimmed === (task.brief || "")) { setEditingField(null); return; }

    setEditingField(null);
    setSavingField(true);
    setFieldError("");
    const prev = { ...task };

    // Optimistic update
    if (field === "title") setTask(t => ({ ...t, title: trimmed }));
    if (field === "assignee") {
      const u = assignableUsers.find(x => x.id === trimmed);
      setTask(t => ({ ...t, assignedToUserId: trimmed, assignedToUserName: u?.name || t.assignedToUserName }));
    }
    if (field === "dueDate") setTask(t => ({ ...t, dueDate: trimmed || null }));
    if (field === "priority") setTask(t => ({ ...t, priority: trimmed }));
    if (field === "brief") setTask(t => ({ ...t, brief: trimmed || null }));

    try {
      const res = await apiFetch(`/api/tasks/${taskId}`, {
        method: "PUT",
        body: JSON.stringify({
          title: field === "title" ? trimmed : task.title,
          brief: field === "brief" ? (trimmed || null) : (task.brief || null),
          assignedToUserId: field === "assignee" ? trimmed : task.assignedToUserId,
          dueDate: field === "dueDate" ? (trimmed || null) : (task.dueDate || null),
          priority: field === "priority" ? trimmed : (task.priority || "Medium"),
          status: task.status,
          projectId: task.projectId || null,
          expectedOutcome: task.expectedOutcome || null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
    } catch (err) {
      setTask(prev);
      setFieldError(err.message || "Failed to save");
    } finally {
      setSavingField(false);
    }
  };

  const getNextStatus = () => {
    if (!task) return null;
    if (task.status === "Open" && canAdvanceStatus) return "InProgress";
    if (task.status === "InProgress" && canAdvanceStatus) return "UnderValidation";
    if (task.status === "UnderValidation" && canValidateAndClose) return "Completed";
    return null;
  };

  const markAsDone = async () => {
    const next = getNextStatus();
    if (!next || updating) return;
    setStatusError("");
    setUpdating(true);

    // Optimistic update: show new status instantly
    const prevTask = task;
    setTask((t) => ({ ...t, status: next }));

    try {
      const res = await apiFetch(`/api/tasks/${taskId}/status`, {
        method: "PUT",
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg);
      }
      // Re-fetch to get updated statusHistory
      const refreshed = await apiFetch(`/api/tasks/${taskId}`);
      if (refreshed.ok) setTask(await refreshed.json());
    } catch (err) {
      setTask(prevTask); // revert on failure
      setStatusError("Update failed: " + err.message);
    } finally {
      setUpdating(false);
    }
  };

  const reopenTask = async () => {
    if (updating) return;
    setStatusError("");
    setUpdating(true);
    const prevTask = task;
    setTask((t) => ({ ...t, status: "Open" }));
    try {
      const res = await apiFetch(`/api/tasks/${taskId}/status`, {
        method: "PUT",
        body: JSON.stringify({ status: "Open" }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg);
      }
      const refreshed = await apiFetch(`/api/tasks/${taskId}`);
      if (refreshed.ok) setTask(await refreshed.json());
    } catch (err) {
      setTask(prevTask);
      setStatusError("Reopen failed: " + err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleCommentChange = (val) => {
    setCommentText(val);
    const match = val.match(/@([\w\s]*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setShowMentions(true);
    } else {
      setShowMentions(false);
      setMentionQuery("");
    }
  };

  const insertMention = (name) => {
    const updated = commentText.replace(/@([\w\s]*)$/, `@${name} `);
    setCommentText(updated);
    setShowMentions(false);
    setMentionQuery("");
  };

  const mentionedUsers = showMentions
    ? assignableUsers
        .filter(u => u.name?.toLowerCase().includes(mentionQuery.toLowerCase()))
        .slice(0, 6)
    : [];

  const submitComment = async () => {
    if (!commentText.trim() || postingComment) return;
    setCommentError("");
    setShowMentions(false);
    setPostingComment(true);
    try {
      const res = await apiFetch(`/api/tasks/${taskId}/comments`, {
        method: "POST",
        body: JSON.stringify({ commentText }),
      });
      if (!res.ok) throw new Error("Failed to post comment");
      const comment = await res.json();
      setComments((prev) => [...prev, comment]);
      setCommentText("");
    } catch (err) {
      setCommentError("Could not post comment: " + err.message);
    } finally {
      setPostingComment(false);
    }
  };

  const deleteTask = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      const res = await apiFetch(`/api/tasks/${taskId}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await res.text() || "Delete failed");
      navigate("/tasks");
    } catch (err) {
      setDeleteError(err.message || "Could not delete task.");
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const priorityColor = {
    High:   { bg: PRIORITY_TAG_BG.High,   color: PRIORITY_TAG_COLORS.High },
    Medium: { bg: PRIORITY_TAG_BG.Medium, color: PRIORITY_TAG_COLORS.Medium },
    Low:    { bg: PRIORITY_TAG_BG.Low,    color: PRIORITY_TAG_COLORS.Low },
  };

  if (loading) {
    return (
      <div className="glass-card" style={{ maxWidth: 800 }}>
        <div style={{ padding: 16 }}>
          <Loader
            type="skeleton"
            height={22}
            width="45%"
            style={{ marginBottom: 18 }}
          />
          <Loader
            type="skeleton"
            height={14}
            width="65%"
            style={{ marginBottom: 10 }}
          />
          <Loader type="skeleton" height={14} width="55%" />
        </div>
      </div>
    );
  }

  if (!task) {
    return <div style={{ padding: 24, color: "var(--text-2)" }}>Task not found.</div>;
  }

  const assigneeName = task.assignedToUserName || "Unassigned";
  const dueDate = task.dueDate
    ? new Date(task.dueDate).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

  const momInfo = task.createdFromMomId
    ? {
        title: task.createdFromMomTitle || "MOM",
        date: task.createdFromMomMeetingDate
          ? new Date(task.createdFromMomMeetingDate).toLocaleDateString(
              "en-IN",
              { day: "numeric", month: "short", year: "numeric" },
            )
          : "—",
      }
    : null;

  const pColor = priorityColor[task.priority] || priorityColor.Medium;

  return (
    <div className="task-details-card" style={{ maxWidth: 1120, margin: "0 auto" }}>

      {/* Breadcrumb */}
      <div style={{ marginBottom: 20, display: "flex", alignItems: "center", gap: 6 }}>
        <button className="btn-ghost" onClick={() => navigate("/tasks")} style={{ padding: "6px 12px", borderRadius: 10, fontSize: 13 }}>
          ← Tasks
        </button>
        {task.title && (
          <>
            <span style={{ color: "var(--text-3)", fontSize: 13 }}>/</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-2)", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {task.title}
            </span>
          </>
        )}
        {isAdmin && (
          <div style={{ marginLeft: "auto" }}>
            <button
              onClick={() => { setDeleteError(""); setShowDeleteConfirm(true); }}
              style={{ padding: "6px 14px", borderRadius: 10, border: "1px solid #fecaca", background: "var(--danger-bg)", color: "var(--danger-text)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              🗑 Delete Task
            </button>
          </div>
        )}
      </div>

      {/* Two-column layout */}
      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>

        {/* ── MAIN CONTENT (left) ── */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Hero card: title + status + actions */}
          <div className="glass-card" style={{ padding: "26px 28px" }}>

            {/* Project badge */}
            <div style={{ marginBottom: 14 }}>
              {task.projectId ? (
                <span className="status-pill status-project">
                  Project:{" "}
                  <Link to={`/projects/${task.projectId}`} className="project-link">
                    {project === undefined ? "Loading..." : (project?.name || "Unknown project")}
                  </Link>
                </span>
              ) : (
                <span style={{ background: "var(--surface-3)", color: "var(--text-2)", padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                  Standalone Task
                </span>
              )}
            </div>

            {/* Title row */}
            <div className="task-details-header" style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap", marginBottom: 6 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {editingField === "title" ? (
                  <div>
                    <input
                      autoFocus
                      value={editValue}
                      maxLength={200}
                      onChange={e => setEditValue(e.target.value)}
                      onBlur={() => saveField("title", editValue)}
                      onKeyDown={e => { if (e.key === "Enter") saveField("title", editValue); if (e.key === "Escape") setEditingField(null); }}
                      style={{ fontSize: 21, fontWeight: 700, padding: "4px 8px", borderRadius: 8, border: "2px solid var(--brand)", outline: "none", width: "100%", boxSizing: "border-box" }}
                    />
                    <div style={{ textAlign: "right", fontSize: 11, color: editValue.length > 180 ? "#dc2626" : "var(--text-3)", marginTop: 2 }}>
                      {editValue.length} / 200
                    </div>
                  </div>
                ) : (
                  <div
                    style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: canEdit ? "pointer" : "default", minWidth: 0 }}
                    onClick={() => startEdit("title")}
                    title={canEdit ? "Click to edit title" : undefined}
                  >
                    <h2 className="task-title" style={{ margin: 0, fontSize: 21, fontWeight: 700, color: "var(--text)", lineHeight: 1.35, wordBreak: "break-word", overflowWrap: "break-word" }}>
                      {task.title}
                    </h2>
                    {canEdit && <span style={{ fontSize: 12, color: "var(--text-3)", flexShrink: 0, marginTop: 4 }}>✎</span>}
                  </div>
                )}
              </div>

              <div style={{ flexShrink: 0, paddingTop: 3 }}>
                <StatusPill status={task.status} />
              </div>
            </div>

            {fieldError && (
              <div style={{ marginBottom: 12, fontSize: 12, color: "var(--danger-text)", padding: "6px 12px", background: "var(--danger-soft)", borderRadius: 6 }}>
                {fieldError}
              </div>
            )}

            {/* Brief / Short Description */}
            {editingField === "brief" ? (
              <div style={{ marginTop: 14 }}>
                <textarea
                  autoFocus
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  onBlur={() => saveField("brief", editValue)}
                  onKeyDown={e => { if (e.key === "Escape") setEditingField(null); }}
                  placeholder="Add a short description…"
                  rows={3}
                  style={{ width: "100%", fontSize: 14, borderRadius: 8, border: "2px solid var(--brand)", outline: "none", padding: "8px 10px", resize: "vertical", boxSizing: "border-box", color: "var(--text)", background: "var(--surface)", fontFamily: "inherit", lineHeight: 1.55 }}
                />
                <div style={{ textAlign: "right", fontSize: 11, color: editValue.length > 280 ? "#dc2626" : "var(--text-3)", marginTop: 2 }}>
                  {editValue.length} / 300
                </div>
              </div>
            ) : (
              <div
                onClick={() => canEdit && startEdit("brief")}
                style={{ marginTop: 14, cursor: canEdit ? "pointer" : "default", borderRadius: 8, padding: "8px 10px", background: task.brief ? "var(--surface-2, #f8fafc)" : "transparent", border: task.brief ? "1px solid var(--border)" : "1px dashed var(--border)", transition: "border-color 0.15s" }}
                title={canEdit ? "Click to edit brief" : undefined}
              >
                {task.brief ? (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ flex: 1, fontSize: 14, color: "var(--text-2)", lineHeight: 1.6 }}>{task.brief}</div>
                    {canEdit && <span style={{ fontSize: 11, color: "var(--text-3)", flexShrink: 0, marginTop: 2 }}>✎</span>}
                  </div>
                ) : canEdit ? (
                  <div style={{ fontSize: 13, color: "var(--text-3)", fontStyle: "italic" }}>+ Add a brief description…</div>
                ) : null}
              </div>
            )}

            {/* Expected Outcome */}
            {task.expectedOutcome && (
              <div style={{ marginTop: 16, padding: "13px 16px", background: "linear-gradient(135deg,#f0f9ff 0%,#f8faff 100%)", borderRadius: 10, border: "1px solid #bae6fd" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#0369a1", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 5 }}>
                  Expected Outcome
                </div>
                <div style={{ fontSize: 14, color: "#1e3a5f", lineHeight: 1.6 }}>{task.expectedOutcome}</div>
              </div>
            )}

            {/* Action buttons */}
            {(canAdvanceStatus || canValidateAndClose || task.status === "Completed") && (
              <div style={{ marginTop: 22, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", paddingTop: 18, borderTop: "1px solid var(--border)" }}>
                <button
                  className="primary"
                  onClick={markAsDone}
                  disabled={task.status === "Completed" || !getNextStatus() || updating}
                  style={{ padding: "10px 22px", fontSize: 14, fontWeight: 600, borderRadius: 10 }}
                >
                  {updating && <Loader size="sm" inline style={{ marginRight: 8 }} />}
                  {task.status === "Open" && "▶ Start Task"}
                  {task.status === "InProgress" && "↑ Submit for Validation"}
                  {task.status === "UnderValidation" && (canValidateAndClose ? "✔ Validate & Close" : "⏳ Waiting for Validation")}
                  {task.status === "Completed" && "✅ Completed"}
                </button>
                {task.status !== "Completed" && (isAssignee || isAssigneesManager) && (
                  <button
                    onClick={() => { setShowTimelineModal(true); setTimelineError(""); }}
                    style={{ padding: "9px 16px", borderRadius: 10, border: "1px solid #d1d5db", background: "white", cursor: "pointer", fontSize: 13, fontWeight: 500 }}
                  >
                    📅 Request Timeline Change
                  </button>
                )}
                {isAdmin && task.status === "Completed" && (
                  <button
                    onClick={reopenTask}
                    disabled={updating}
                    style={{ padding: "9px 16px", borderRadius: 10, border: "1px solid #d1d5db", background: "white", cursor: updating ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 500 }}
                  >
                    ↩ Reopen Task
                  </button>
                )}
              </div>
            )}

            {statusError && (
              <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
                {statusError}
              </div>
            )}
          </div>

          {/* Evidence card */}
          <div className="glass-card" style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: 15 }}>📎</span>
              <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--text)" }}>Evidence</h4>
            </div>

            {task.status !== "Completed" && canAddEvidence && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", gap: 2, marginBottom: 12, background: "var(--surface-2,#f3f4f6)", borderRadius: 8, padding: 3, width: "fit-content" }}>
                  <button onClick={() => setEvidenceTab("url")} style={{ padding: "5px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer", background: evidenceTab === "url" ? "white" : "transparent", color: evidenceTab === "url" ? "#1d4ed8" : "var(--text-2)", boxShadow: evidenceTab === "url" ? "0 1px 4px rgba(0,0,0,0.1)" : "none" }}>
                    🔗 Google Doc / Link
                  </button>
                  <button onClick={() => setEvidenceTab("file")} style={{ padding: "5px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer", background: evidenceTab === "file" ? "white" : "transparent", color: evidenceTab === "file" ? "#1d4ed8" : "var(--text-2)", boxShadow: evidenceTab === "file" ? "0 1px 4px rgba(0,0,0,0.1)" : "none" }}>
                    📎 File Upload
                  </button>
                </div>

                {evidenceTab === "url" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <input
                      value={evidenceUrl}
                      onChange={(e) => setEvidenceUrl(e.target.value)}
                      placeholder="Paste Google Docs / Sheets / Drive URL..."
                      style={{ fontSize: 13 }}
                    />
                    <div style={{ display: "flex", gap: 8 }}>
                      <input value={evidenceUrlLabel} onChange={(e) => setEvidenceUrlLabel(e.target.value)} placeholder="Label (optional, e.g. 'Q3 Report')" style={{ flex: 1, fontSize: 13 }} />
                      <button onClick={submitEvidenceUrl} disabled={addingUrl || !evidenceUrl.trim()} style={{ padding: "8px 16px", background: "#2563eb", color: "white", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                        {addingUrl ? "Adding..." : "Add Link"}
                      </button>
                    </div>
                  </div>
                )}

                {evidenceTab === "file" && (
                  <div>
                    <label style={{ display: "inline-block", padding: "8px 16px", background: "#2563eb", color: "white", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
                      {uploadingFile ? "Uploading..." : "📎 Upload Evidence"}
                      <input type="file" multiple style={{ display: "none" }} onChange={uploadEvidence} disabled={uploadingFile} />
                    </label>
                    <span style={{ fontSize: 12, color: "var(--text-2)", marginLeft: 10 }}>PDF, Images, DOCX, XLSX, ZIP (max 10MB each)</span>
                  </div>
                )}
              </div>
            )}

            {evidenceError && (
              <div style={{ padding: "8px 12px", borderRadius: 8, marginBottom: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
                {evidenceError}
              </div>
            )}

            {evidenceFiles.length > 0 ? (
              <div style={{ display: "grid", gap: 6 }}>
                {evidenceFiles.map((entry, i) => {
                  let isLink = false, linkUrl = "", linkLabel = "";
                  try {
                    if (entry.startsWith("{")) {
                      const parsed = JSON.parse(entry);
                      isLink = true; linkUrl = parsed.url; linkLabel = parsed.label || parsed.url;
                    }
                  } catch { /* not JSON */ }
                  if (isLink) {
                    const isGoogleDoc = linkUrl.includes("docs.google.com") || linkUrl.includes("drive.google.com");
                    return (
                      <a key={i} href={linkUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: "#2563eb", textDecoration: "none", display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", background: "#f0f9ff", borderRadius: 8, border: "1px solid #bae6fd" }}>
                        {isGoogleDoc ? "📋" : "🔗"} {linkLabel}
                        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-2)" }}>↗ Open</span>
                      </a>
                    );
                  }
                  const fileName = entry.startsWith("http") ? decodeURIComponent(entry.split("/").pop()) : entry;
                  return <EvidenceFileLink key={i} taskId={taskId} fileName={fileName} />;
                })}
              </div>
            ) : (
              <div style={{ color: "var(--text-3)", fontSize: 13, fontStyle: "italic" }}>No evidence added yet.</div>
            )}
          </div>

          {/* Comments card */}
          <div className="glass-card" style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: 15 }}>💬</span>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--text)" }}>Comments</h3>
            </div>

            {canComment && (
              <>
                <div style={{ position: "relative", marginBottom: commentError ? 6 : 12 }}>
                  <div style={{ display: "flex", gap: 10 }}>
                    <input
                      value={commentText}
                      onChange={(e) => { handleCommentChange(e.target.value); if (commentError) setCommentError(""); }}
                      placeholder="Add a comment… (type @ to mention someone)"
                      style={{ flex: 1 }}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") { setShowMentions(false); return; }
                        if (e.key === "Enter" && !e.shiftKey && !showMentions) { e.preventDefault(); submitComment(); }
                      }}
                    />
                    <button className="primary" onClick={submitComment} disabled={postingComment}>
                      {postingComment ? "Posting..." : "Post"}
                    </button>
                  </div>

                  {/* @mention dropdown */}
                  {showMentions && mentionedUsers.length > 0 && (
                    <div style={{
                      position: "absolute", top: "100%", left: 0, right: 48,
                      background: "var(--surface, #fff)",
                      border: "1px solid var(--border, #e5e7eb)",
                      borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                      zIndex: 100, overflow: "hidden", marginTop: 4,
                    }}>
                      {mentionedUsers.map(u => (
                        <button
                          key={u.id}
                          onMouseDown={(e) => { e.preventDefault(); insertMention(u.name); }}
                          style={{
                            width: "100%", display: "flex", alignItems: "center", gap: 10,
                            padding: "8px 12px", background: "none", border: "none",
                            cursor: "pointer", textAlign: "left",
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "var(--surface2, #f8f9fb)"}
                          onMouseLeave={e => e.currentTarget.style.background = "none"}
                        >
                          <div style={{
                            width: 28, height: 28, borderRadius: "50%",
                            background: "#e2e8f0", color: "#475569",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: 700, flexShrink: 0,
                          }}>
                            {(u.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text, #111)" }}>{u.name}</div>
                            <div style={{ fontSize: 11, color: "var(--text-3, #9ca3af)" }}>{u.role}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {commentError && (
                  <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
                    {commentError}
                  </div>
                )}
              </>
            )}

            {loadingComments ? (
              <div style={{ padding: 10 }}>
                <Loader type="skeleton" height={12} width="70%" style={{ marginBottom: 8 }} />
                <Loader type="skeleton" height={12} width="55%" />
              </div>
            ) : comments.length === 0 ? (
              <div style={{ color: "var(--text-3)", fontSize: 13, fontStyle: "italic" }}>No comments yet.</div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {comments.map((c) => (
                  <div key={c.id} style={{ padding: "12px 14px", border: "1px solid #eef2f7", borderRadius: 12, background: "rgba(255,255,255,0.75)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#2563eb", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                        {(c.createdByName?.[0] || "?").toUpperCase()}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{c.createdByName || "Unknown"}</span>
                      <span style={{ fontSize: 11, color: "var(--text-3)", marginLeft: "auto" }}>
                        {new Date(c.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: "var(--text-2)", paddingLeft: 36 }}>
                      {(c.commentText || "").split(/(@\w[\w\s]*\w(?=\s|$)|\S)/g).map((part, i) =>
                        part.startsWith("@")
                          ? <span key={i} style={{ color: "var(--brand, #0891b2)", fontWeight: 600 }}>{part}</span>
                          : part
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Timeline Change Requests card */}
          {timelineRequests.length > 0 && (
            <div className="glass-card" style={{ padding: "20px 24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: 15 }}>📅</span>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--text)" }}>Timeline Change Requests</h3>
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {timelineRequests.map((r) => (
                  <div key={r.id} style={{ padding: "12px 14px", border: "1px solid var(--border-strong)", borderRadius: 12, background: "white" }}>
                    <div style={{ fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span><strong>Requested:</strong>{" "}{new Date(r.requestedDueDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                      <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: r.status === "Approved" ? "#d1fae5" : r.status === "Rejected" ? "var(--danger-bg)" : "#fef3c7", color: r.status === "Approved" ? "var(--success-text)" : r.status === "Rejected" ? "var(--danger-text)" : "var(--warn-text)" }}>
                        {r.status}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 4 }}>{r.reason}</div>
                    {r.status === "Pending" && canReviewTimeline && (
                      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                        <button style={{ padding: "4px 12px", background: "#16a34a", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12 }} onClick={() => openReviewModal(r.id, "approve")}>✅ Approve</button>
                        <button style={{ padding: "4px 12px", background: "var(--danger)", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12 }} onClick={() => openReviewModal(r.id, "reject")}>❌ Reject</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status History card */}
          <div className="glass-card" style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: 15 }}>🕐</span>
              <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--text)" }}>Status History</h4>
            </div>
            {Array.isArray(task.statusHistory) && task.statusHistory.length > 0 ? (
              <div style={{ display: "grid", gap: 8 }}>
                {task.statusHistory.map((h) => (
                  <div key={h.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(255,255,255,0.7)", border: "1px solid rgba(0,0,0,0.06)", borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>
                    <span style={{ fontWeight: 600, color: "var(--text-2)" }}>{h.action}</span>
                    <span style={{ color: "var(--text-3)", fontSize: 12 }}>
                      {h.performedAt ? new Date(h.performedAt).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: "var(--text-3)", fontSize: 13, fontStyle: "italic" }}>No history yet.</div>
            )}
          </div>

        </div>{/* end main content */}

        {/* ── SIDEBAR (right) ── */}
        <div style={{ width: 272, flexShrink: 0 }}>
          <div className="glass-card" style={{ padding: "20px 20px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 18 }}>
              Task Details
            </div>

            {/* Assignee */}
            <div className="meta-item" style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span className="meta-label" style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Assignee</span>
                {canEdit && <span style={{ fontSize: 11, color: "var(--brand)", cursor: "pointer", fontWeight: 600 }} onClick={() => !savingField && startEdit("assignee")}>Edit</span>}
              </div>
              {editingField === "assignee" ? (
                <select autoFocus value={editValue} onChange={e => saveField("assignee", e.target.value)} onBlur={() => setEditingField(null)} onKeyDown={e => e.key === "Escape" && setEditingField(null)} style={{ fontSize: 13, borderRadius: 6, padding: "3px 6px", width: "100%" }}>
                  {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              ) : (
                <div className="assignee" style={{ display: "flex", alignItems: "center", gap: 8, cursor: canEdit ? "pointer" : "default" }} onClick={() => !savingField && startEdit("assignee")}>
                  <div className="avatar">{assigneeName[0].toUpperCase()}</div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{assigneeName}</span>
                </div>
              )}
            </div>

            <div style={{ height: 1, background: "var(--border)", margin: "0 0 14px" }} />

            {/* Due Date */}
            <div className="meta-item" style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span className="meta-label" style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Due Date</span>
                {canEdit && <span style={{ fontSize: 11, color: "var(--brand)", cursor: "pointer", fontWeight: 600 }} onClick={() => !savingField && startEdit("dueDate")}>Edit</span>}
              </div>
              {editingField === "dueDate" ? (
                <input autoFocus type="date" value={editValue} min={new Date().toLocaleDateString('en-CA')} onChange={e => setEditValue(e.target.value)} onBlur={() => saveField("dueDate", editValue)} onKeyDown={e => { if (e.key === "Enter") saveField("dueDate", editValue); if (e.key === "Escape") setEditingField(null); }} style={{ fontSize: 13, borderRadius: 6, padding: "3px 6px", width: "100%", boxSizing: "border-box" }} />
              ) : (
                <div className="meta-value" style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", cursor: canEdit ? "pointer" : "default" }} onClick={() => !savingField && startEdit("dueDate")}>
                  {dueDate}
                </div>
              )}
            </div>

            <div style={{ height: 1, background: "var(--border)", margin: "0 0 14px" }} />

            {/* Priority */}
            <div className="meta-item" style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span className="meta-label" style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Priority</span>
                {canEdit && <span style={{ fontSize: 11, color: "var(--brand)", cursor: "pointer", fontWeight: 600 }} onClick={() => !savingField && startEdit("priority")}>Edit</span>}
              </div>
              {editingField === "priority" ? (
                <select autoFocus value={editValue} onChange={e => saveField("priority", e.target.value)} onBlur={() => setEditingField(null)} onKeyDown={e => e.key === "Escape" && setEditingField(null)} style={{ fontSize: 13, borderRadius: 6, padding: "3px 6px", width: "100%" }}>
                  <option value="High">🔴 High</option>
                  <option value="Medium">🟡 Medium</option>
                  <option value="Low">🟢 Low</option>
                </select>
              ) : (
                <div className="meta-value" style={{ cursor: canEdit ? "pointer" : "default" }} onClick={() => !savingField && startEdit("priority")}>
                  <span style={{ ...pColor, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{task.priority || "Medium"}</span>
                </div>
              )}
            </div>

            <div style={{ height: 1, background: "var(--border)", margin: "0 0 14px" }} />

            {/* Created From */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Created From</div>
              {momInfo ? (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, background: "#ede9fe", color: "#6d28d9", padding: "1px 7px", borderRadius: 20, flexShrink: 0 }}>MOM</span>
                    <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 170 }} title={momInfo.title}>{momInfo.title}</span>
                  </div>
                  {momInfo.date !== "—" && <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3 }}>{momInfo.date}</div>}
                </div>
              ) : (
                <span style={{ fontSize: 13, color: "var(--text-2)" }}>Manual</span>
              )}
            </div>

            {task.assignedByUserName && (
              <>
                <div style={{ height: 1, background: "var(--border)", margin: "0 0 14px" }} />
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Assigned By</div>
                  <div style={{ fontSize: 13, color: "var(--text)" }}>{task.assignedByUserName}</div>
                </div>
              </>
            )}

            <div style={{ height: 1, background: "var(--border)", margin: "0 0 14px" }} />

            <div style={{ marginBottom: task.status === "Completed" && task.completedAt ? 16 : 0 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Created</div>
              <div style={{ fontSize: 13, color: "var(--text)" }}>
                {task.createdAt ? new Date(task.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
              </div>
            </div>

            {task.status === "Completed" && task.completedAt && (
              <>
                <div style={{ height: 1, background: "var(--border)", margin: "0 0 14px" }} />
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Completed On</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--success)" }}>
                    {new Date(task.completedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>{/* end sidebar */}

      </div>{/* end two-column layout */}

      {/* Timeline change modal */}
      {showTimelineModal && (
        <div className="modal-overlay" onClick={() => { setShowTimelineModal(false); setTimelineError(""); }}
          onKeyDown={(e) => { if (e.key === "Escape") { setShowTimelineModal(false); setTimelineError(""); } }}>
          <div className="glass-card modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Request Timeline Change</h3>
              <button className="modal-close" onClick={() => { setShowTimelineModal(false); setTimelineError(""); }}>✕</button>
            </div>
            {timelineError && (
              <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
                {timelineError}
              </div>
            )}
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 13, color: "var(--text-2)" }}>New Due Date *</label>
              <input type="date" value={timelineDate} min={new Date().toLocaleDateString('en-CA')} onChange={(e) => { setTimelineDate(e.target.value); if (timelineError) setTimelineError(""); }} style={{ width: "100%", marginTop: 4 }} />
              {timelineDate && new Date(timelineDate) < new Date(new Date().toDateString()) && (
                <div style={{ marginTop: 4, fontSize: 12, color: "#d97706" }}>⚠ Selected date is in the past</div>
              )}
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 13, color: "var(--text-2)" }}>Reason *</label>
              <textarea value={timelineReason} onChange={(e) => setTimelineReason(e.target.value)} rows={3} placeholder="Why is a timeline change needed?" style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 8, border: "1px solid var(--border-strong)", fontFamily: "inherit" }} />
            </div>
            <div className="modal-actions">
              <button onClick={() => { setShowTimelineModal(false); setTimelineError(""); }}>Cancel</button>
              <button className="primary" onClick={submitTimelineRequest} disabled={submittingTimeline}>
                {submittingTimeline ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Comment Modal */}
      {reviewModal && (
        <div className="modal-overlay" onClick={() => setReviewModal(null)}>
          <div className="glass-card modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440, width: "95vw" }}>
            <div className="modal-header">
              <h3>
                {reviewModal.action === "approve" ? "✅ Approve Request" : "❌ Reject Request"}
              </h3>
              <button className="modal-close" onClick={() => { setReviewModal(null); setReviewError(""); }}>✕</button>
            </div>
            {reviewError && (
              <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
                {reviewError}
              </div>
            )}
            <label style={{ fontSize: 13, color: "var(--text-2)", display: "block", marginBottom: 6 }}>
              Comment {reviewModal.action === "reject" ? "*" : "(optional)"}
            </label>
            <textarea autoFocus value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder={reviewModal.action === "approve" ? "Add a note (optional)..." : "Reason for rejection..."} rows={3} style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border-strong)", fontFamily: "inherit", fontSize: 13, resize: "vertical", boxSizing: "border-box" }} />
            <div className="modal-actions">
              <button className="btn-ghost" onClick={() => { setReviewModal(null); setReviewError(""); }}>Cancel</button>
              <button
                onClick={submitReview}
                disabled={submittingReview || (reviewModal.action === "reject" && !reviewComment.trim())}
                style={{ padding: "8px 20px", borderRadius: 10, border: "none", fontWeight: 600, fontSize: 13, cursor: "pointer", background: reviewModal.action === "approve" ? "#16a34a" : "#dc2626", color: "white", opacity: (submittingReview || (reviewModal.action === "reject" && !reviewComment.trim())) ? 0.5 : 1 }}
              >
                {submittingReview
                  ? reviewModal.action === "approve" ? "Approving..." : "Rejecting..."
                  : reviewModal.action === "approve" ? "Approve" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="glass-card" style={{ width: 400, padding: 28, borderRadius: 18, boxShadow: "0 25px 60px rgba(0,0,0,0.14)" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: 10, fontSize: 16, fontWeight: 700 }}>Delete Task?</h3>
            <p style={{ color: "var(--text-2)", fontSize: 13, marginBottom: 22 }}>
              Permanently delete <strong>{task?.title}</strong>? This cannot be undone.
            </p>
            {deleteError && (
              <div style={{ marginBottom: 14, padding: "8px 12px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
                {deleteError}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button className="btn-ghost" style={{ padding: "9px 16px", borderRadius: 10 }} onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button
                onClick={deleteTask}
                disabled={deleting}
                style={{ padding: "9px 20px", borderRadius: 10, border: "none", fontWeight: 600, fontSize: 13, cursor: "pointer", background: "var(--danger)", color: "white", opacity: deleting ? 0.6 : 1 }}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
